// TodayPanel: the Overview right panel (DESIGN §3) — TODAY (deadlines due today/overdue, then the newest
// activity) and UPCOMING (next 7 days: batch due dates + Main Calendar events, max 6). Data: derive.todayPanel.
//   <Page aside={<TodayPanel state={state} index={index} today={today} />}>
// Also exported for reuse: TodaySection, UpcomingSection, DeadlineItem, UpcomingList, shortDay.
import './today-panel.css';
import * as derive from '../../shared/derive.js';
import { formatDate } from '../../shared/dates.js';
import { workspaceHref } from '../router.js';
import { ActivityItem } from './activity-item.jsx';
import { RightPanelSection } from './right-panel.jsx';
import { cx } from './ui.jsx';

const DEADLINE_LIMIT = 5;
const ACTIVITY_LIMIT = 7;
// Deadlines come first; activity then fills the list to ~8 rows (never fewer than 3), so Upcoming stays near the top.
const activityRoom = (deadlines) => Math.min(ACTIVITY_LIMIT, Math.max(3, 8 - deadlines));
const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// '2026-09-26' → 'Sat 26' (title attribute carries the full date).
export function shortDay(date) {
  const [wd, md = ''] = formatDate(date, 'weekday').split(', ');
  return `${wd} ${md.split(' ')[1] || ''}`.trim();
}

// One overdue / due-today batch: clock glyph + name, "2 days overdue · Ryan" (the words carry the state, not only colour).
export function DeadlineItem({ item }) {
  const overdue = item.due?.overdue;
  const owner = item.owner ? firstName(item.ownerName) : 'Unassigned';
  return (
    <ActivityItem
      icon="clock"
      tone={overdue ? 'danger' : 'warning'}
      text={item.title}
      href={item.href}
      meta={
        <>
          <span class={overdue ? 'text-danger' : 'text-warning'}>{item.due?.text || 'Due today'}</span>
          {` · ${owner}`}
        </>
      }
    />
  );
}

export function TodaySection({ deadlines = [], activity = [], today }) {
  const shown = deadlines.slice(0, DEADLINE_LIMIT);
  const more = deadlines.length - shown.length;
  const recent = activity.slice(0, activityRoom(shown.length));
  const empty = !deadlines.length && !recent.length;
  return (
    <RightPanelSection title="Today" empty="Nothing needs attention today.">
      {empty ? null : (
        <div class="tp-list">
          {shown.map((d) => <DeadlineItem key={d.id} item={d} />)}
          {more > 0 && <a class="tp-more link-quiet" href={workspaceHref({})}>{`+${more} more due`}</a>}
          {shown.length > 0 && recent.length > 0 && <hr class="tp-sep" />}
          {recent.map((a) => (
            <ActivityItem key={a.id} name={a.actorName} text={a.text} at={a.at} today={today} href={a.href} />
          ))}
        </div>
      )}
    </RightPanelSection>
  );
}

// Compact one-line rows: day (shown once per date) · dot (blue = calendar event, grey = batch due) · title · time.
export function UpcomingList({ items = [] }) {
  return (
    <ul class="tp-upcoming">
      {items.map((u, i) => {
        const first = i === 0 || items[i - 1].date !== u.date;
        return (
          <li key={u.id}>
            <a class={cx('tp-up', first && i > 0 && 'is-new-day')} href={u.href}>
              <span class={cx('tp-up-day', !first && 'is-repeat')} title={formatDate(u.date, 'full')}>
                {shortDay(u.date)}
              </span>
              <span class={cx('tp-up-dot', u.kind === 'event' ? 'is-event' : 'is-due')} aria-hidden="true" />
              <span class="tp-up-title">{u.title}</span>
              {u.time && <span class="tp-up-time">{u.time}</span>}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

export function UpcomingSection({ items = [] }) {
  return (
    <RightPanelSection title="Upcoming" empty="Nothing in the next 7 days.">
      {items.length ? <UpcomingList items={items} /> : null}
    </RightPanelSection>
  );
}

export function TodayPanel({ state, index, today }) {
  const data = derive.todayPanel(state, index, today);
  return (
    <>
      <TodaySection deadlines={data.deadlines} activity={data.activity} today={today} />
      <UpcomingSection items={data.upcoming} />
    </>
  );
}
